import React, { type ReactNode, Suspense } from 'react';

import { FormattedMessage } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';

import { useAppContext } from '../../../../common/ui/app-context';
import { Link } from '../../../../common/ui/link-component';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';

import { messages } from './messages';

const Image = React.lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/search-error-image" */ './image'
		),
);

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 'space.200',
	color: 'color.text',
});

const imageFallbackStyles = xcss({
	width: '124px',
	height: '124px',
});

const headingStyles = xcss({
	font: 'font.heading.small',
	fontWeight: 'font.weight.medium',
	marginTop: 'space.100',
});

const bodyStyles = xcss({
	marginTop: 'space.100',
	font: 'font.body',
});

const pressableStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	font: 'font.body',
	color: 'color.link',
	padding: 'space.0',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'underline',
		cursor: 'pointer',
	},
});

type ErrorScreenProps = {
	onRetry: () => void;
};

export const NoResultsScreen = ({ onRetry }: ErrorScreenProps): React.JSX.Element => {
	const { generateSearchUrl, queryParams } = useAppContext();
	const quickFindQuery = useQuickFindQuery();

	return (
		<Box xcss={containerStyles}>
			<Suspense fallback={<Box xcss={imageFallbackStyles} />}>
				{/* eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details */}
				<Image />
			</Suspense>
			<Box as="h2" xcss={headingStyles}>
				<FormattedMessage
					{...messages.noResultsHeading}
					values={{
						button: (s: ReactNode[]) => (
							<Pressable xcss={pressableStyles} onClick={onRetry}>
								{s}
							</Pressable>
						),
					}}
				/>
			</Box>
			<Box xcss={bodyStyles}>
				<FormattedMessage
					{...messages.noResultsBody}
					values={{
						link: (s: ReactNode[]) => (
							<Link
								xcss={pressableStyles}
								href={generateSearchUrl({
									...queryParams,
									text: quickFindQuery,
								})}
							>
								{s}
							</Link>
						),
					}}
				/>
			</Box>
		</Box>
	);
};
